import { WorkHistoryLogo } from '@seek/certsy-ui';
import { CertsyDialog } from '@seek/profile-base-ui';
import { useTranslations } from '@vocab/react';
import { Box, List, Text } from 'braid-design-system';
import { useCallback, useEffect } from 'react';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import type { AnalyticsVerificationsSeekPassDialogCallToAction } from 'src/modules/analytics/types';

import type { VerificationsSeekPassDialogAnalyticsOrigin } from '../analyticsUtils';

import translations from './.vocab';

interface Props {
  url: string;
  showDialog: boolean;
  closeDialogFn: () => void;
  origin: VerificationsSeekPassDialogAnalyticsOrigin;
  onSeekPassDialogClick: (arg: {
    callToAction: AnalyticsVerificationsSeekPassDialogCallToAction;
  }) => void;
}

export const VerifyWorkHistoryDialog = ({
  url,
  showDialog,
  closeDialogFn,
  origin,
  onSeekPassDialogClick,
}: Props) => {
  const { t } = useTranslations(translations);

  const { seekPassDialogDisplayed } = useAnalytics();

  const onDialogDisplay = useCallback(() => {
    seekPassDialogDisplayed({ origin, certsyType: 'workplace' });
  }, [origin, seekPassDialogDisplayed]);

  const onClose = () => {
    closeDialogFn();
  };

  const onCertsyClick = () => {
    onSeekPassDialogClick({
      callToAction: 'verify on certsy',
    });
  };

  const onMaybeLaterClick = () => {
    onSeekPassDialogClick({ callToAction: 'maybe later' });
  };

  useEffect(() => {
    if (showDialog) {
      onDialogDisplay();
    }
  }, [onDialogDisplay, showDialog]);

  const certsyDialogText = (
    <Box paddingX="small">
      <List>
        <Text>
          Boost the credibility of your SEEK Profile by adding legitimacy to
          your work experience.
        </Text>
        <Text>Verify with as little as your work email address.</Text>
        <Text>{t('Trust in SEEK Pass, a 100% SEEK-owned platform.')}</Text>
        <Text>
          {t('Showcase the SEEK Pass supertick on your Shared Profile.')}
        </Text>
      </List>
    </Box>
  );

  return (
    <CertsyDialog
      title={t('Verify your workplace')}
      bodyText={certsyDialogText}
      certsyButtonText={t('Verify on SEEK Pass')}
      seekPassLogo={<WorkHistoryLogo />}
      certsyUrl={url}
      width="small"
      open={showDialog}
      onCertsyClick={onCertsyClick}
      onMaybeLaterClick={onMaybeLaterClick}
      onClose={onClose}
      data={{ automation: 'workplace-verify-dialog' }}
    />
  );
};
